import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import api from "@/lib/api";

/**
 * MentionTextarea
 * A drop-in textarea replacement that opens a floating @-mention picker
 * when the user types `@<query>`. Arrow keys + Enter to select; Esc to close.
 *
 * Props mirror a normal <textarea>: value, onChange, placeholder, rows, etc.
 */
const MentionTextarea = forwardRef(function MentionTextarea(
  {
    value,
    onChange,
    placeholder = "",
    rows = 2,
    maxLength = 2000,
    className = "",
    "data-testid": testId,
  },
  ref,
) {
  const textareaRef = useRef(null);
  const [openAt, setOpenAt] = useState(null); // { start, query }
  const [matches, setMatches] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const debounceRef = useRef(null);

  // Forward ref to the underlying textarea so callers can focus it
  useImperativeHandle(ref, () => textareaRef.current, []);

  const closeDropdown = () => {
    setOpenAt(null);
    setMatches([]);
    setActiveIdx(0);
  };

  // Detect `@query` immediately before the caret
  const detectMention = (text, caret) => {
    if (caret == null) return null;
    // Walk back from caret until whitespace or @
    let i = caret - 1;
    while (i >= 0) {
      const ch = text[i];
      if (ch === "@") {
        // Must be start of string or preceded by whitespace
        if (i === 0 || /\s/.test(text[i - 1])) {
          const query = text.slice(i + 1, caret);
          // Limit query to mention-safe chars only
          if (/^[a-zA-Z0-9_.\-]{0,30}$/.test(query)) {
            return { start: i, query };
          }
        }
        return null;
      }
      if (/\s/.test(ch)) return null;
      i--;
    }
    return null;
  };

  const handleChange = (e) => {
    onChange(e);
    const el = textareaRef.current;
    if (!el) return;
    const det = detectMention(e.target.value, el.selectionStart);
    if (det) {
      setOpenAt(det);
      setActiveIdx(0);
    } else {
      closeDropdown();
    }
  };

  const handleKeyDown = (e) => {
    if (!openAt) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(0, matches.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" || e.key === "Tab") {
      if (matches[activeIdx]) {
        e.preventDefault();
        applyMention(matches[activeIdx]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeDropdown();
    }
  };

  // Re-detect mention on click / arrow-key navigation
  const handleSelect = () => {
    const el = textareaRef.current;
    if (!el) return;
    const det = detectMention(el.value, el.selectionStart);
    if (det) {
      setOpenAt(det);
    } else {
      closeDropdown();
    }
  };

  // Debounced API search
  useEffect(() => {
    if (!openAt) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/users/search", {
          params: { q: openAt.query || "", limit: 6 },
        });
        setMatches(Array.isArray(data) ? data : []);
        setActiveIdx(0);
      } catch {
        setMatches([]);
      }
    }, 140);
    return () => clearTimeout(debounceRef.current);
  }, [openAt]);

  const applyMention = (u) => {
    const el = textareaRef.current;
    if (!el || !openAt) return;
    const text = el.value;
    const before = text.slice(0, openAt.start);
    const after = text.slice(el.selectionStart);
    const insert = `@${u.name} `;
    const next = `${before}${insert}${after}`;
    // Synthetic event for the caller's onChange
    onChange({ target: { value: next } });
    closeDropdown();
    // Restore caret position after the inserted mention
    requestAnimationFrame(() => {
      el.focus();
      const pos = (before + insert).length;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onSelect={handleSelect}
        onBlur={() => setTimeout(closeDropdown, 120)}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        data-testid={testId}
        className={className}
      />
      {openAt && matches.length > 0 && (
        <ul
          data-testid="mention-dropdown"
          className="absolute z-50 left-0 right-0 sm:right-auto sm:min-w-[260px] mt-1 bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-lg shadow-2xl overflow-hidden"
        >
          {matches.map((u, i) => (
            <li
              key={u.user_id}
              data-testid={`mention-option-${u.name}`}
              onMouseDown={(e) => {
                // Use mousedown so it fires before the textarea blur closes the menu
                e.preventDefault();
                applyMention(u);
              }}
              onMouseEnter={() => setActiveIdx(i)}
              className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm
                ${i === activeIdx ? "bg-[var(--tt-primary)]/15 text-white" : "hover:bg-white/5 text-zinc-200"}`}
            >
              {u.picture ? (
                <img src={u.picture} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[var(--tt-primary)] to-[var(--tt-purple)] flex items-center justify-center text-[10px] font-bold shrink-0">
                  {(u.name || "?")[0].toUpperCase()}
                </div>
              )}
              <span className="truncate">@{u.name}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

export default MentionTextarea;
